import React, { useState } from 'react';
import './TransparencyMetrics.css';

const TransparencyMetrics = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState(null);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`;
    }
    return num.toLocaleString();
  };

  // Format SOL amounts
  const formatSOL = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M SOL`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(2)}K SOL`;
    }
    return `${amount.toFixed(2)} SOL`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Calculate additional metrics
  const calculateMetrics = () => {
    if (!data.platform) return {};

    const avgAUMPerPool = data.platform.totalAUM / (data.platform.totalPools || 1);
    const avgInvestmentPerUser = data.platform.totalInvested / (data.platform.totalInvestors || 1);
    const utilizationRate = (data.platform.totalInvested / data.platform.totalAUM) * 100;

    return {
      avgAUMPerPool,
      avgInvestmentPerUser,
      utilizationRate
    };
  };

  const additionalMetrics = calculateMetrics();

  // Metric definitions with descriptions
  const metricDefinitions = {
    totalPools: {
      title: 'Total Investment Pools',
      description: 'Number of active investment pools available on the platform',
      category: 'Platform Scale'
    },
    totalManagers: {
      title: 'Pool Managers',
      description: 'Number of verified and unverified pool managers',
      category: 'Platform Scale'
    },
    totalAUM: {
      title: 'Assets Under Management',
      description: 'Total value of all assets managed across all pools',
      category: 'Financial Metrics'
    },
    totalInvestors: {
      title: 'Active Investors',
      description: 'Number of users with active investments in pools',
      category: 'Platform Scale'
    },
    totalInvested: {
      title: 'Total Invested Capital',
      description: 'Total amount invested by users across all pools',
      category: 'Financial Metrics'
    },
    platformAverageReturn: {
      title: 'Platform Average Return',
      description: 'Average return across all active investment pools',
      category: 'Performance'
    },
    totalTrades24h: {
      title: '24h Trading Volume',
      description: 'Number of trades executed across all pools in the last 24 hours',
      category: 'Trading Activity'
    },
    totalVolume24h: {
      title: '24h SOL Volume',
      description: 'Total SOL volume traded across all pools in the last 24 hours',
      category: 'Trading Activity'
    },
    uniqueTokensTraded24h: {
      title: 'Unique Tokens (24h)',
      description: 'Number of different tokens traded in the last 24 hours',
      category: 'Trading Activity'
    },
    totalManagementFeesCollected30d: {
      title: 'Management Fees (30d)',
      description: 'Total management fees collected across all pools in the last 30 days',
      category: 'Fee Transparency'
    },
    totalPerformanceFeesCollected30d: {
      title: 'Performance Fees (30d)',
      description: 'Total performance fees collected across all pools in the last 30 days',
      category: 'Fee Transparency'
    }
  };

  // Group metrics by category
  const groupedMetrics = {
    'Platform Scale': [],
    'Financial Metrics': [],
    'Performance': [],
    'Trading Activity': [],
    'Fee Transparency': []
  };

  // Populate grouped metrics
  Object.entries(metricDefinitions).forEach(([key, definition]) => {
    if (data.platform && data.platform[key] !== undefined) {
      groupedMetrics[definition.category].push({
        key,
        value: data.platform[key],
        ...definition
      });
    } else if (data.trading && data.trading[key] !== undefined) {
      groupedMetrics[definition.category].push({
        key,
        value: data.trading[key],
        ...definition
      });
    } else if (data.fees && data.fees[key] !== undefined) {
      groupedMetrics[definition.category].push({
        key,
        value: data.fees[key],
        ...definition
      });
    }
  });

  // Format metric value based on type
  const formatMetricValue = (key, value) => {
    switch (key) {
      case 'totalAUM':
      case 'totalInvested':
      case 'totalVolume24h':
      case 'totalManagementFeesCollected30d':
      case 'totalPerformanceFeesCollected30d':
        return formatSOL(value);
      case 'platformAverageReturn':
        return formatPercentage(value);
      case 'totalPools':
      case 'totalManagers':
      case 'totalInvestors':
      case 'totalTrades24h':
      case 'uniqueTokensTraded24h':
        return formatNumber(value);
      default:
        return value.toLocaleString();
    }
  };

  // Get metric color based on category
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Platform Scale': return '#3B82F6';
      case 'Financial Metrics': return '#10B981';
      case 'Performance': return '#8B5CF6';
      case 'Trading Activity': return '#F59E0B';
      case 'Fee Transparency': return '#EF4444';
      default: return '#6B7280';
    }
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="transparency-metrics empty">
        <div className="empty-state">
          <h4>No Transparency Data Available</h4>
          <p>Platform transparency metrics will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transparency-metrics">
      <div className="metrics-header">
        <h3>Platform Transparency Metrics</h3>
        <p>Real-time insights into platform performance and activity</p>
      </div>

      <div className="metrics-overview">
        <div className="overview-cards">
          <div className="overview-card highlight">
            <div className="card-icon">🏦</div>
            <div className="card-content">
              <div className="card-value">{formatSOL(data.platform?.totalAUM || 0)}</div>
              <div className="card-label">Total AUM</div>
            </div>
          </div>
          
          <div className="overview-card highlight">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <div className="card-value">{formatNumber(data.platform?.totalInvestors || 0)}</div>
              <div className="card-label">Active Investors</div>
            </div>
          </div>
          
          <div className="overview-card highlight">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <div className="card-value">
                {formatPercentage(data.platform?.platformAverageReturn || 0)}
              </div>
              <div className="card-label">Avg Return</div>
            </div>
          </div>
          
          <div className="overview-card highlight">
            <div className="card-icon">💹</div>
            <div className="card-content">
              <div className="card-value">{formatNumber(data.trading?.totalTrades24h || 0)}</div>
              <div className="card-label">24h Trades</div>
            </div>
          </div>
        </div>
      </div>

      <div className="metrics-categories">
        {Object.entries(groupedMetrics).map(([category, metrics]) => {
          if (metrics.length === 0) return null;
          
          return (
            <div key={category} className="metrics-category">
              <div 
                className="category-header"
                style={{ borderLeftColor: getCategoryColor(category) }}
              >
                <h4>{category}</h4>
                <span className="metric-count">{metrics.length} metrics</span>
              </div>
              
              <div className="category-metrics">
                {metrics.map(metric => (
                  <div 
                    key={metric.key}
                    className={`metric-item ${selectedMetric === metric.key ? 'selected' : ''}`}
                    onClick={() => setSelectedMetric(
                      selectedMetric === metric.key ? null : metric.key
                    )}
                  >
                    <div className="metric-main">
                      <div className="metric-value">
                        {formatMetricValue(metric.key, metric.value)}
                      </div>
                      <div className="metric-title">{metric.title}</div>
                    </div>
                    
                    {selectedMetric === metric.key && (
                      <div className="metric-description">
                        <p>{metric.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="additional-insights">
        <h4>Platform Insights</h4>
        <div className="insights-grid">
          <div className="insight-item">
            <span className="insight-label">Average AUM per Pool:</span>
            <span className="insight-value">
              {formatSOL(additionalMetrics.avgAUMPerPool || 0)}
            </span>
          </div>
          
          <div className="insight-item">
            <span className="insight-label">Average Investment per User:</span>
            <span className="insight-value">
              {formatSOL(additionalMetrics.avgInvestmentPerUser || 0)}
            </span>
          </div>
          
          <div className="insight-item">
            <span className="insight-label">Capital Utilization Rate:</span>
            <span className="insight-value">
              {additionalMetrics.utilizationRate?.toFixed(1) || 0}%
            </span>
          </div>
          
          <div className="insight-item">
            <span className="insight-label">Total Fees Collected (30d):</span>
            <span className="insight-value">
              {formatSOL(
                (data.fees?.totalManagementFeesCollected30d || 0) + 
                (data.fees?.totalPerformanceFeesCollected30d || 0)
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="transparency-footer">
        <div className="data-freshness">
          <div className="freshness-indicator">
            <span className="indicator-dot active"></span>
            <span className="freshness-text">
              Data updated: {new Date(data.lastUpdated || Date.now()).toLocaleString()}
            </span>
          </div>
        </div>
        
        <div className="transparency-commitment">
          <h5>Our Transparency Commitment</h5>
          <ul>
            <li>All performance data is calculated in real-time</li>
            <li>Fee structures are clearly disclosed for every pool</li>
            <li>Trading activity is publicly auditable (anonymized)</li>
            <li>No hidden fees or undisclosed charges</li>
          </ul>
        </div>
        
        <div className="data-sources">
          <p>
            <small>
              Data sources: On-chain transactions, pool performance calculations, 
              real-time trading feeds. All financial data is independently verifiable.
            </small>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransparencyMetrics;